import { IPlatformProject } from './platformApiTypes';

/**
 * Cleans a GitHub URL by removing quotes and .git suffix
 * @param url - The URL to clean
 * @returns Clean GitHub URL
 */
function cleanGitHubUrl(url: string): string {
  let cleanUrl = url.trim();

  // Remove quotes from beginning and end
  cleanUrl = cleanUrl.replace(/^["']|["']$/g, '');

  // Remove .git suffix
  cleanUrl = cleanUrl.replace(/\.git$/, '');

  // Remove trailing punctuation that might be part of text formatting
  cleanUrl = cleanUrl.replace(/[.,;!?]+$/, '');

  return cleanUrl;
}

/**
 * Extracts repository metadata from description text
 * Parses source_repo and last_synced fields from description
 *
 * @param description - The project description text to parse
 * @returns Object containing githubUrl and lastSynced if found, null values otherwise
 */
export function parseRepositoryMetadataFromDescription(description: string): {
  githubUrl: string | null;
  lastSynced: string | null;
} {
  if (description === null || description === undefined || description === '' || typeof description !== 'string') {
    return { githubUrl: null, lastSynced: null };
  }

  // Clean and normalize the description
  const normalizedDescription = description.trim();

  // Pattern to match source_repo field in YAML-like format, handling quoted URLs
  const sourceRepoPattern = /source_repo\s*:\s*["']?(https:\/\/github\.com\/[^\s\n"']+)["']?/i;
  const sourceRepoMatch = normalizedDescription.match(sourceRepoPattern);

  let githubUrl: string | null = null;
  if (sourceRepoMatch?.[1] !== undefined && sourceRepoMatch[1] !== '') {
    const url = sourceRepoMatch[1].trim();
    const cleanUrl = cleanGitHubUrl(url);
    githubUrl = isValidGitHubUrl(cleanUrl) ? cleanUrl : null;
  }

  // Pattern to match last_synced field in YAML-like format, handling quoted values
  // Captures complete timestamps including spaces, colons, periods, T separator, timezone offset, and UTC Z
  const lastSyncedPattern = /last_synced\s*:\s*["']?([0-9\-\s:T.+Z]+)["']?/i;
  const lastSyncedMatch = normalizedDescription.match(lastSyncedPattern);

  let lastSynced: string | null = null;
  if (lastSyncedMatch?.[1] !== undefined && lastSyncedMatch[1] !== '') {
    lastSynced = lastSyncedMatch[1].trim();
  }

  return { githubUrl, lastSynced };
}

/**
 * Returns the appropriate repository URL for a project
 * Returns GitHub URL if found in description, otherwise returns DOL Platform web_url
 *
 * @param project - The platform project to get URL for
 * @returns Repository URL (GitHub if detected, otherwise DOL Platform)
 */
export function getRepositoryUrl(project: IPlatformProject): string {
  const metadata = parseRepositoryMetadataFromDescription(project.description);

  if (metadata.githubUrl !== null && metadata.githubUrl !== '') {
    return metadata.githubUrl;
  }

  return project.web_url;
}

/**
 * Validates if a URL is safe for external linking
 * @param url - The URL to validate
 * @returns boolean indicating if the URL is safe
 */
export function isValidExternalUrl(url: string | null | undefined): boolean {
  if (url === null || url === undefined || url === '' || typeof url !== 'string') return false;

  try {
    const parsedUrl = new URL(url);
    // Only allow http/https protocols for security
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Validates if a URL is a valid GitHub repository URL
 *
 * @param url - The URL to validate
 * @returns True if the URL is a valid GitHub repository URL
 *
 * @example
 * ```typescript
 * isValidGitHubUrl('https://github.com/user/repo'); // true
 * isValidGitHubUrl('https://github.com/user'); // false (no repo name)
 * isValidGitHubUrl('https://gitlab.com/user/repo'); // false (not GitHub)
 * ```
 */
export function isValidGitHubUrl(url: string): boolean {
  if (url === null || url === undefined || url === '' || typeof url !== 'string') {
    return false;
  }

  try {
    const parsedUrl = new URL(url);

    // Must be HTTPS GitHub
    if (parsedUrl.protocol !== 'https:' || parsedUrl.hostname !== 'github.com') {
      return false;
    }

    // Path should be in format /owner/repo or /owner/repo.git
    const pathPattern = /^\/[^/\s]+\/[^/\s]+(?:\.git)?(?:\/.*)?$/;

    if (!pathPattern.test(parsedUrl.pathname)) {
      return false;
    }

    // Extract owner and repo name for additional validation
    const pathParts = parsedUrl.pathname.split('/').filter((part) => part.length > 0);

    if (pathParts.length < 2) {
      return false;
    }

    const owner = pathParts[0];
    const repo = pathParts[1].replace(/\.git$/, ''); // Remove .git suffix if present

    // Basic validation: owner and repo should not be empty and contain valid characters
    const validNamePattern = /^[a-zA-Z0-9._-]+$/;

    if (!validNamePattern.test(owner) || !validNamePattern.test(repo)) {
      return false;
    }

    // Additional checks for common invalid patterns
    if (owner.startsWith('.') || owner.endsWith('.') || repo.startsWith('.') || repo.endsWith('.')) {
      return false;
    }

    return true;
  } catch (error) {
    // Invalid URL format
    return false;
  }
}
